/**
 * DocumentWorkspace (Phase 8i.2) — interactive workspace for a template_document.
 *
 * Renders the SNAPSHOTTED rendered_content (never re-rendered live) inside a
 * Shadow DOM so the document's own styles are isolated from the app. Interactive
 * controls are hydrated onto the {{signature}} / {{choicebox}} placeholders by
 * their stable data-block-id (sig-N / choice-N). Each control is mounted with an
 * isolated React root so events fire reliably inside the shadow tree; the shared
 * ApiClient is passed in as a prop (the mounted roots are outside app context).
 *
 * 'sent' locks everything (enforced here AND on the server): canvases become
 * static signature images and choiceboxes show only the selected option.
 */

import React, { useEffect, useRef, useState } from 'react';
import { CRM, tintOf } from '../lib/theme';
import { createRoot, Root } from 'react-dom/client';
import { useApiClient } from '../hooks/useApi';
import { Icons } from '../lib/icons';
import ApiClient from '../services/api';
import { formatMarkTime } from '../lib/utils';
import { formatDocNo } from '../lib/jobNumber';

const D = { ...CRM };

// Colors for controls that render on the WHITE document sheet (not the dark app).
const P = { ink: '#1a1f2b', line: '#111827', border: '#d1d5db', label: '#374151', accent: '#3b82f6', muted: '#6b7280' };

interface DocumentWorkspaceProps {
  doc: any; // documents row (template_document)
  onClose: () => void;
  onChanged: () => void;
}

/**
 * The typed-signature face, declared ON THE DOCUMENT rather than in the shadow.
 *
 * @font-face inside a shadow root is ignored — font faces are document-scoped,
 * not tree-scoped. The snapshot's own stylesheet carries the identical rule
 * (backend/lib/documentStyles) and it goes into the shadow with everything
 * else, so it has never had any effect here: a typed mark would have fallen
 * through 'BVL Signature' to 'Dancing Script' (not installed on a Mac) to
 * cursive, silently, in exactly the way this project has already paid for once.
 *
 * Declared once, on first open, and left in place. Same self-hosted file and
 * same two names as the document and the portal, so a typed signature is the
 * same face in the workspace, the portal and the PDF.
 */
const SIG_FONT_STYLE_ID = 'mf-signature-face';
const SIG_FONT_STACK = "'BVL Signature', 'Dancing Script', cursive";

function ensureSignatureFace(): void {
  if (typeof document === 'undefined' || document.getElementById(SIG_FONT_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = SIG_FONT_STYLE_ID;
  el.textContent = `@font-face{font-family:'BVL Signature';src:url('/fonts/DancingScript-Variable.ttf') format('truetype');font-weight:400 700;font-style:normal;font-display:block;}`;
  document.head.appendChild(el);
}

/**
 * WHERE THIS MARK CAME FROM, in the words the caption uses.
 *
 * applied_via has recorded this since migration 028 and no screen in the CRM
 * has ever shown it — the only place a rep could tell a client's signature from
 * one a colleague applied was the completion certificate, inside the PDF.
 *
 * 'unknown' GETS NO SUFFIX. Those are the pre-028 rows whose origin was never
 * captured, and 028 kept them a separate value precisely so they would not be
 * asserted to be something they might not be. Saying nothing is the honest
 * rendering of not knowing; the caption simply reads as it did before.
 */
function originSuffix(row: any): string {
  if (row?.applied_via === 'portal') return ' · client portal';
  if (row?.applied_via === 'internal') return ' · internal';
  return '';
}

/**
 * IS THIS MARK TYPED? Deliberately the same test as the server's fill
 * (backend/lib/documentFill.ts), character for character: `input_kind ===
 * 'typed' || !signature_data`. The workspace and the PDF must reach the same
 * answer about the same row, and the way they stop doing that is by each
 * asking a slightly different question.
 *
 * The second half matters on its own. A row with no image is typed whatever its
 * input_kind claims — rendering an <img> with a null src is how a real signed
 * contract came to show a broken-image icon where the client's name should be.
 */
function isTypedMark(row: any): boolean {
  return row?.input_kind === 'typed' || !row?.signature_data;
}

// Pull the <style> text and the .sheet inner HTML out of a full snapshot doc.
function extractSnapshot(html: string): { styles: string; sheetHtml: string } {
  try {
    const dp = new DOMParser().parseFromString(html, 'text/html');
    const styles = Array.from(dp.querySelectorAll('style')).map((s) => s.textContent || '').join('\n');
    const sheet = dp.querySelector('.sheet');
    return { styles, sheetHtml: sheet ? sheet.innerHTML : dp.body?.innerHTML || html };
  } catch {
    return { styles: '', sheetHtml: html };
  }
}

export function DocumentWorkspace({ doc, onClose, onChanged }: DocumentWorkspaceProps) {
  const api = useApiClient();
  const hostRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);
  const rootsRef = useRef<Root[]>([]);
  const sigsRef = useRef<Record<string, any>>({}); // block_id -> signature row
  const choicesRef = useRef<Record<string, string>>({}); // block_id -> selected_option

  const [status, setStatus] = useState<string>(doc.status);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * COMPLETION LOCKS THE DOCUMENT, AND status DOES NOT SAY SO. Completing stamps
   * completed_at and leaves status as 'draft' — see migration 028 §5 for why
   * signature state is a separate axis from status. So this used to read
   * `status !== 'draft'` and a signed, completed, client-emailed contract opened
   * here fully editable: every signature pad live, every choicebox re-selectable.
   * The server matched the mistake, so a rep could overwrite a client's
   * signature and the upsert would replace it rather than add to it.
   *
   * Two separate facts. `isComplete` is the one that matters most, so it also
   * decides which banner shows.
   */
  const isComplete = doc.completed_at != null;
  const locked = status !== 'draft' || isComplete;

  const title = doc.template_name || 'Document';

  // Load existing signatures + choices once.
  useEffect(() => {
    ensureSignatureFace();
    let alive = true;
    (async () => {
      try {
        const res = await api.getDocumentInputs(doc.id);
        const data = res.data || { signatures: [], choices: [] };
        const sm: Record<string, any> = {};
        for (const s of data.signatures) sm[s.block_id] = s;
        const cm: Record<string, string> = {};
        for (const ch of data.choices) cm[ch.block_id] = ch.selected_option;
        sigsRef.current = sm;
        choicesRef.current = cm;
      } catch {
        /* ignore — start with empty inputs */
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [doc.id]);

  // Render the snapshot into the shadow root and hydrate interactive controls.
  useEffect(() => {
    if (!loaded || !hostRef.current) return;
    const host = hostRef.current;
    if (!shadowRef.current) shadowRef.current = host.attachShadow({ mode: 'open' });
    const shadow = shadowRef.current;

    const { styles, sheetHtml } = extractSnapshot(doc.rendered_content || '');
    shadow.innerHTML = `<style>${styles}\n.sheet{box-shadow:none;border-radius:0;margin:0;max-width:none;padding:32px 40px}</style><div class="sheet">${sheetHtml}</div>`;

    const nodes = Array.from(shadow.querySelectorAll('[data-input]')) as HTMLElement[];
    for (const node of nodes) {
      const kind = node.getAttribute('data-input');
      const blockId = node.getAttribute('data-block-id') || '';
      node.setAttribute('style', 'margin:18px 0'); // reset the placeholder frame
      node.innerHTML = '';
      const root = createRoot(node);
      rootsRef.current.push(root);
      if (kind === 'signature') {
        root.render(
          <SignatureBlock
            api={api}
            docId={doc.id}
            blockId={blockId}
            locked={locked}
            initial={sigsRef.current[blockId] ?? null}
            onSaved={(row) => { sigsRef.current[blockId] = row; }}
          />
        );
      } else if (kind === 'initials') {
        root.render(
          <InitialsBlock
            api={api}
            docId={doc.id}
            blockId={blockId}
            locked={locked}
            initial={sigsRef.current[blockId] ?? null}
            onSaved={(row) => { sigsRef.current[blockId] = row; }}
          />
        );
      } else if (kind === 'choicebox') {
        const options = (node.getAttribute('data-options') || '').split('|').map((o) => o.trim()).filter(Boolean);
        root.render(
          <ChoiceboxBlock
            api={api}
            docId={doc.id}
            blockId={blockId}
            options={options}
            locked={locked}
            initial={choicesRef.current[blockId] ?? ''}
            onSaved={(opt) => { choicesRef.current[blockId] = opt; }}
          />
        );
      }
    }
    return () => {
      rootsRef.current.forEach((r) => { try { r.unmount(); } catch { /* noop */ } });
      rootsRef.current = [];
    };
  }, [loaded, locked, doc.id, doc.rendered_content]);

  const markSent = async () => {
    if (!window.confirm('Mark as sent? The document will be locked and can no longer be edited.')) return;
    setBusy(true);
    try {
      await api.updateDocumentStatus(doc.id, 'sent');
      setStatus('sent');
      onChanged();
    } catch (e: any) {
      window.alert(e?.message || 'Failed to mark sent');
    } finally {
      setBusy(false);
    }
  };

  const voidDoc = async () => {
    if (!window.confirm('Void this document? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.updateDocumentStatus(doc.id, 'void');
      setStatus('void');
      onChanged();
      onClose();
    } catch (e: any) {
      window.alert(e?.message || 'Failed to void');
    } finally {
      setBusy(false);
    }
  };

  /**
   * D2 — the SERVER PDF, not a browser print dialog.
   *
   * This used to open a new window and hand it HTML to print, which produced a
   * different artifact for every rep depending on their browser, ignored the
   * page CSS, and stamped "about:blank" across a legal contract. It now
   * downloads the same file the client gets from the portal — one renderer, one
   * file. Failure surfaces inline; it is never a silent no-op.
   */
  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const printExport = async () => {
    setBusy(true); setPdfErr(null);
    try {
      const { blob, filename } = await api.downloadDocumentPdf(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      setPdfErr(e?.message || 'Could not generate the PDF.');
    } finally {
      setBusy(false);
    }
  };

  // THE PILL SAYS THE STRONGEST TRUE THING. A signed, completed, client-emailed
  // contract reading "draft" — directly above a banner saying it is signed and
  // read only — is the interface contradicting itself about the one fact on the
  // screen that matters. Same rule as the documents list's StatusBadge.
  const statusLabel = status === 'void' ? 'void' : isComplete ? 'complete' : status;
  const statusColor = status === 'void' ? D.dim : isComplete ? D.green : status === 'sent' ? D.accent : D.muted;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'stretch' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: D.bg, width: '100%', maxWidth: '900px', display: 'flex', flexDirection: 'column', borderLeft: `1px solid ${D.border}`, borderRight: `1px solid ${D.border}` }}>
        {/* Header */}
        <div style={{ background: D.panel, borderBottom: `1px solid ${D.border}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: D.text }}>Document workspace</div>
            <div style={{ fontSize: '12px', color: D.muted, fontFamily: "'DM Mono', monospace" }}>
              {title} · {formatDocNo(doc.number)} · <span style={{ color: statusColor, textTransform: 'capitalize' }}>{statusLabel}</span>
            </div>
          </div>
          <button onClick={() => { onChanged(); onClose(); }} disabled={busy} style={btn(D.panel2, D.text)}>Save and close</button>
          <button onClick={printExport} disabled={busy} style={btn(D.panel2, D.muted)}>
            {busy ? 'Preparing…' : 'Download PDF'}
          </button>
          {/* "Mark sent" is a DRAFT action — it locks a document so it can no
              longer be edited. A completed document is already locked and
              already with the client, so the button could only move it
              backwards into a state that describes it less accurately. */}
          {status === 'draft' && !isComplete && <button onClick={markSent} disabled={busy} style={btn(D.accentFill, 'var(--crm-on-fill)')}>Mark sent</button>}
          {status !== 'void' && <button onClick={voidDoc} disabled={busy} style={btn(D.panel2, D.red)}>Void</button>}
          <button onClick={onClose} style={btn(D.panel2, D.muted)} aria-label="Close"><Icons.close size={16} /></button>
        </div>

        {pdfErr && (
          <div style={{ background: `${tintOf(D.redFill, 9)}`, borderBottom: `1px solid ${tintOf(D.redLine, 27)}`, color: D.red, fontSize: '11px', padding: '6px 16px' }}>
            {pdfErr}
          </div>
        )}

        {/* Locked banner. A completed document says so FIRST — it is the stronger
            fact, and a signed contract reading "marked sent" would describe the
            lesser thing that happened to it. */}
        {locked && (
          <div style={{
            background: isComplete ? `${tintOf(D.greenFill, 9)}` : `${tintOf(D.amberFill, 9)}`,
            borderBottom: `1px solid ${tintOf(isComplete ? D.greenLine : D.amber, 27)}`,
            color: isComplete ? D.green : D.amber,
            fontSize: '11px', padding: '6px 16px', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            {isComplete
              ? <><Icons.check size={13} aria-hidden /><b>Signed — read only.</b> This document is signed and complete. To change it, void it and send a new one.</>
              : status === 'sent' ? 'This document is marked sent and is locked. Signatures and choices can no longer be edited.' : 'This document is void.'}
          </div>
        )}

        {/* Document body (shadow-isolated snapshot + hydrated controls) */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#f4f5f7', padding: '16px' }}>
          {!loaded ? (
            <div style={{ color: D.dim, textAlign: 'center', padding: '40px' }}>Loading…</div>
          ) : (
            <div style={{ background: 'var(--crm-white)', maxWidth: '820px', margin: '0 auto', borderRadius: '6px', boxShadow: '0 2px 12px var(--crm-shadow-color)' }}>
              <div ref={hostRef} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Signature block (hand-rolled canvas; mouse + touch; retina-safe) -------

function SignatureBlock({ api, docId, blockId, locked, initial, onSaved }: {
  api: ApiClient; docId: string; blockId: string; locked: boolean; initial: any; onSaved: (row: any) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [drawn, setDrawn] = useState(false);
  const [name, setName] = useState<string>(initial?.signed_name || '');
  const [signed, setSigned] = useState<any>(initial || null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const showCanvas = !locked && (!signed || editing);

  // Configure the canvas (retina) and attach native pointer listeners. Touch
  // listeners are non-passive so we can preventDefault (stop the page scrolling).
  useEffect(() => {
    if (!showCanvas) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cssW = 400, cssH = 120;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = P.ink; // dark ink → legible on white when printed
    setDrawn(false);

    const pos = (e: MouseEvent | TouchEvent) => {
      const r = canvas.getBoundingClientRect();
      const t = (e as TouchEvent).touches?.[0];
      const cx = t ? t.clientX : (e as MouseEvent).clientX;
      const cy = t ? t.clientY : (e as MouseEvent).clientY;
      return { x: cx - r.left, y: cy - r.top };
    };
    const start = (e: MouseEvent | TouchEvent) => {
      drawingRef.current = true;
      const { x, y } = pos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e: MouseEvent | TouchEvent) => {
      if (!drawingRef.current) return;
      if ((e as TouchEvent).touches) e.preventDefault(); // don't scroll while signing
      const { x, y } = pos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      setDrawn(true);
    };
    const end = () => { drawingRef.current = false; };

    canvas.addEventListener('mousedown', start);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, [showCanvas]);

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    setDrawn(false);
  };

  const confirm = async () => {
    if (!drawn || !name.trim() || !canvasRef.current) return;
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const res = await api.upsertSignature(docId, { block_id: blockId, signature_data: dataUrl, signed_name: name.trim() });
      const row = res.data;
      setSigned(row);
      setEditing(false);
      onSaved(row);
    } catch (e: any) {
      window.alert(e?.message || 'Failed to save signature');
    } finally {
      setBusy(false);
    }
  };

  const frame: React.CSSProperties = { border: `1px solid ${P.border}`, borderRadius: '8px', padding: '12px', maxWidth: '440px', background: 'var(--crm-white)' };

  // Signed (and not re-signing) — static preview.
  if (signed && !editing) {
    return (
      <div style={frame}>
        <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: P.muted, marginBottom: '6px' }}>Signature</div>
        {isTypedMark(signed)
          ? (
            // A TYPED SIGNATURE IS A SIGNATURE (UETA §2(8)), and it is not "a
            // drawn one that failed to have an image". This used to render an
            // <img> regardless, so every typed signature — the portal's own
            // "type your name" path — showed a broken-image icon on a document
            // the client had properly signed.
            <div style={{ fontFamily: SIG_FONT_STACK, fontSize: '30px', lineHeight: 1.3, color: P.ink, wordBreak: 'break-word', minHeight: '40px' }}>
              {signed.typed_value || signed.signed_name || ''}
            </div>
          )
          : <img src={signed.signature_data} alt="signature" style={{ display: 'block', maxHeight: '70px', maxWidth: '100%', background: 'var(--crm-white)' }} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '6px', fontSize: '11px', color: P.muted }}>
          {/* The date carries a zone now, so it is long enough to wrap in a
              narrow pane. Kept whole; the row wraps around it instead. */}
          <span>Signed by <b style={{ color: P.label }}>{signed.signed_name}</b> · <span style={{ whiteSpace: 'nowrap' }}>{formatMarkTime(signed.signed_at)}</span>{originSuffix(signed)}</span>
          {!locked && <button onClick={() => setEditing(true)} style={pbtn(false)}>Re-sign</button>}
        </div>
      </div>
    );
  }

  // Locked and never signed.
  if (locked) {
    return <div style={{ ...frame, color: P.muted, fontSize: '12px' }}>Signature — not signed.</div>;
  }

  // Draft: interactive canvas.
  return (
    <div style={frame}>
      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: P.muted, marginBottom: '6px' }}>Signature</div>
      <canvas ref={canvasRef} style={{ width: '400px', height: '120px', maxWidth: '100%', background: 'var(--crm-white)', border: `1px solid ${P.border}`, borderRadius: '6px', touchAction: 'none', cursor: 'crosshair' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
        <span style={{ fontSize: '11px', color: P.label }}>Name (printed):</span>
        <input value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, padding: '5px 8px', border: `1px solid ${P.border}`, borderRadius: '6px', fontSize: '12px', color: P.ink }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
        <button onClick={clear} style={pbtn(false)}>Clear</button>
        <button onClick={confirm} disabled={busy || !drawn || !name.trim()} style={pbtn(true, busy || !drawn || !name.trim())}>Confirm sig</button>
      </div>
    </div>
  );
}

// ---- Choicebox block -------------------------------------------------------

/**
 * D1 Item 2 — INITIALS. Legally a signature; practically a two-character mark
 * beside a choice, so it gets a two-character affordance rather than the
 * signature block's draw-pad-and-printed-name ceremony.
 *
 * TYPED BY DEFAULT, which is the opposite of the signature block. Nobody draws
 * legible initials with a mouse, and UETA §2(8) asks for a mark made with intent
 * to sign, not for a particular shape of one. The full name is still captured —
 * "JD" identifies nobody on its own, and the completion certificate has to name
 * who initialled.
 */
function InitialsBlock({ api, docId, blockId, locked, initial, onSaved }: {
  api: ApiClient; docId: string; blockId: string; locked: boolean; initial: any; onSaved: (row: any) => void;
}) {
  const [saved, setSaved] = useState<any>(initial);
  const [value, setValue] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);

  const submit = async () => {
    const v = value.trim();
    if (!v) { setErr('Enter your initials.'); return; }
    if (!name.trim()) { setErr('Enter your full name.'); return; }
    setBusy(true); setErr(null); setWarn(null);
    try {
      const res: any = await api.upsertInitials(docId, { block_id: blockId, typed_value: v, signed_name: name.trim() });
      const row = res?.data ?? null;
      setSaved(row);
      onSaved(row);
      // The mark saved but its audit entry did not. NEVER swallowed: a
      // certificate missing an event is exactly the kind of quiet gap that only
      // surfaces in a dispute.
      if (res?.auditWarning) setWarn(res.auditWarning);
    } catch (e: any) {
      setErr(e?.message || 'Could not save your initials.');
    } finally {
      setBusy(false);
    }
  };

  if (saved) {
    const mark = saved.typed_value || saved.signed_name || '';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '54px', height: '34px', padding: '0 10px', border: `1px solid ${P.accent}`, borderRadius: '6px', background: '#eef2ff', color: P.accent, fontWeight: 700, fontSize: '15px', letterSpacing: '1px' }}>
          {isTypedMark(saved) ? mark : <img src={saved.signature_data} alt="Initials" style={{ maxHeight: '26px' }} />}
        </span>
        <span style={{ fontSize: '12px', color: P.muted }}>
          Initialled by <b style={{ color: P.label }}>{saved.signed_name}</b> · <span style={{ whiteSpace: 'nowrap' }}>{formatMarkTime(saved.signed_at)}</span>{originSuffix(saved)}
        </span>
        {warn && <span style={{ fontSize: '11px', color: 'var(--crm-amber)', width: '100%' }}>{warn}</span>}
      </div>
    );
  }

  if (locked) return <span style={{ fontSize: '13px', color: P.muted }}>Not initialled</span>;

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value.toUpperCase().slice(0, 6))}
        placeholder="JD"
        aria-label="Your initials"
        style={{ width: '68px', padding: '7px 9px', textAlign: 'center', letterSpacing: '2px', fontWeight: 700, fontSize: '15px', border: `1px solid ${P.border}`, borderRadius: '6px', color: P.ink, background: 'var(--crm-white)' }}
      />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your full name"
        aria-label="Your full name"
        style={{ flex: 1, minWidth: '170px', padding: '7px 9px', fontSize: '13px', border: `1px solid ${P.border}`, borderRadius: '6px', color: P.ink, background: 'var(--crm-white)' }}
      />
      <button onClick={submit} disabled={busy} style={pbtn(true, busy)}>{busy ? 'Saving…' : 'Initial'}</button>
      {err && <div style={{ fontSize: '12px', color: 'var(--crm-red)', width: '100%' }}>{err}</div>}
    </div>
  );
}

function ChoiceboxBlock({ api, docId, blockId, options, locked, initial, onSaved }: {
  api: ApiClient; docId: string; blockId: string; options: string[]; locked: boolean; initial: string; onSaved: (opt: string) => void;
}) {
  const [sel, setSel] = useState<string>(initial || '');
  const [busy, setBusy] = useState(false);

  const choose = async (opt: string) => {
    if (locked || busy) return;
    setBusy(true);
    try {
      await api.upsertChoice(docId, { block_id: blockId, selected_option: opt });
      setSel(opt);
      onSaved(opt);
    } catch (e: any) {
      window.alert(e?.message || 'Failed to save choice');
    } finally {
      setBusy(false);
    }
  };

  if (locked) {
    return (
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '13px', color: P.ink }}>
        {sel ? <span style={{ padding: '5px 12px', borderRadius: '6px', background: '#eef2ff', border: `1px solid ${P.accent}`, color: P.accent, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Icons.checkboxOn size={14} /> {sel}</span> : <span style={{ color: P.muted }}>No selection</span>}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {options.map((o) => {
        const on = o === sel;
        return (
          <button
            key={o}
            onClick={() => choose(o)}
            disabled={busy}
            style={{ padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', background: on ? P.accent : 'var(--crm-on-fill)', color: on ? 'var(--crm-on-fill)' : P.label, border: `1px solid ${on ? P.accent : P.border}`, fontWeight: on ? 600 : 400 }}
          >
            {on ? <Icons.checkboxOn size={14} /> : <Icons.checkboxOff size={14} />} {o}
          </button>
        );
      })}
    </div>
  );
}

// The fill that used to live here — baking captured signatures into the
// snapshot for export — now lives on the SERVER in lib/documentFill.ts, and this
// copy has been deleted rather than left to drift. D2 replaced this component's
// window.print() export with the server PDF route, which orphaned it: it had no
// callers, while the server route it was replaced by did not fill at all, so a
// completed contract was emailing the client blank signature lines.
// ---- small styles ----------------------------------------------------------

function btn(bg: string, color: string): React.CSSProperties {
  return { padding: '6px 12px', background: bg, color, border: `1px solid ${bg === D.panel2 ? D.border : bg}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
}
function pbtn(primary: boolean, disabled = false): React.CSSProperties {
  return {
    padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: disabled ? 'default' : 'pointer',
    background: primary ? P.accent : 'var(--crm-on-fill)', color: primary ? 'var(--crm-on-fill)' : P.label, border: `1px solid ${primary ? P.accent : P.border}`,
    opacity: disabled ? 0.5 : 1,
  };
}
